import { Video, FileText, Users, TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";

export default function SocialProof() {
  const [stats, setStats] = useState({
    videosAnalyzed: 0,
    reportsGenerated: 0,
    activeUsers: 0,
    avgGrowth: 0
  });

  // Animate numbers on mount
  useEffect(() => {
    const targets = {
      videosAnalyzed: 15234,
      reportsGenerated: 12891,
      activeUsers: 3456,
      avgGrowth: 47
    };

    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setStats({
        videosAnalyzed: Math.floor(targets.videosAnalyzed * progress),
        reportsGenerated: Math.floor(targets.reportsGenerated * progress),
        activeUsers: Math.floor(targets.activeUsers * progress),
        avgGrowth: Math.floor(targets.avgGrowth * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setStats(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const metrics = [
    {
      icon: Video,
      value: stats.videosAnalyzed.toLocaleString(),
      label: "Videos Analizados",
      color: "from-cyan-500 to-purple-500"
    },
    {
      icon: FileText,
      value: stats.reportsGenerated.toLocaleString(),
      label: "Informes Generados",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Users,
      value: stats.activeUsers.toLocaleString(),
      label: "Usuarios Activos",
      color: "from-purple-500 to-cyan-500"
    },
    {
      icon: TrendingUp,
      value: `${stats.avgGrowth}%`,
      label: "Crecimiento Promedio",
      color: "from-pink-500 to-purple-500"
    }
  ];

  return (
    <section data-testid="social-proof-section" className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 text-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-black/20"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Creadores de Contenido Confían en ViralTubeAI
          </h2>
          <p className="text-lg text-purple-200">
            Únete a la comunidad de creadores que optimizan su contenido con IA
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-all hover:scale-105"
              >
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${metric.color} flex items-center justify-center mx-auto mb-4`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold mb-2">{metric.value}</div>
                <div className="text-purple-200">{metric.label}</div>
              </div>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="mt-16 text-center">
          <p className="text-sm text-purple-300 mb-4">Powered by</p>
          <div className="flex flex-wrap justify-center gap-6 items-center opacity-70">
            <span className="text-lg font-semibold">OpenAI GPT-5.2</span>
            <span className="text-gray-400">•</span>
            <span className="text-lg font-semibold">YouTube API</span>
            <span className="text-gray-400">•</span>
            <span className="text-lg font-semibold">AI Analysis</span>
          </div>
        </div>
      </div>
    </section>
  );
}
